// tenant/OrderPages/TenantOrdersPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { useTenantUserInfo } from '../../context/userInfoProvider';

const pageContainer: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: React.CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerRow: React.CSSProperties = {
  marginBottom: 16,
};

const titleStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
  marginTop: 4,
};

const sectionCard: React.CSSProperties = {
  backgroundColor: 'rgba(23, 8, 36, 0.9)',
  borderRadius: 12,
  padding: 16,
  border: '1px solid rgba(255,255,255,0.05)',
};

const listWrapper: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const rowCard: React.CSSProperties = {
  backgroundColor: 'rgba(58, 31, 82, 0.9)',
  borderRadius: 10,
  padding: 12,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'transform 0.08s ease, box-shadow 0.08s ease',
} as React.CSSProperties;

const rowLeft: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
};

const rowTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
};

const rowMeta: React.CSSProperties = {
  fontSize: 13,
  opacity: 0.9,
};

const caret: React.CSSProperties = {
  fontSize: 20,
  opacity: 0.7,
};

const emptyText: React.CSSProperties = {
  fontSize: 14,
  opacity: 0.8,
};

const badge: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  backgroundColor: '#312047',
  color: '#C7B6FF',
  marginLeft: 8,
};

const TenantOrdersPage: React.FC = () => {
  const { fetchData, loading, dataSourcePastOrders } = useTenantUserInfo();
  const [localLoading, setLocalLoading] = useState(true);
  const history = useHistory();

  useEffect(() => {
    const load = async () => {
      setLocalLoading(true);
      await fetchData();
      setLocalLoading(false);
    };
    load();
  }, [fetchData]);

  // Sort past orders newest -> oldest (by date)
  const sortedOrders = useMemo(() => {
    if (!dataSourcePastOrders) return [];
    return [...dataSourcePastOrders].sort((a: any, b: any) => {
      const aDate = a.date ? new Date(a.date).getTime() : 0;
      const bDate = b.date ? new Date(b.date).getTime() : 0;
      return bDate - aDate;
    });
  }, [dataSourcePastOrders]);

  const isLoading = localLoading || loading;

  const goToOrder = (id: string) => {
    history.push(`/tenant/orders/${id}`);
  };

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerRow}>
          <h1 style={titleStyle}>Order history</h1>
          <p style={subtitleStyle}>
            See your past pickups and deliveries with Bins.
          </p>
        </div>

        <section style={sectionCard}>
          {isLoading ? (
            <p>Loading orders…</p>
          ) : !sortedOrders.length ? (
            <p style={emptyText}>
              You don&apos;t have any completed orders yet. Once you complete a
              pickup or delivery, it will show up here.
            </p>
          ) : (
            <div style={listWrapper}>
              {sortedOrders.map((order: any) => {
                const dateLabel = order.date
                  ? moment(order.date).format('dddd, MMMM DD, YYYY')
                  : 'Date unavailable';
                const timeLabel = order.time || 'Time unavailable';
                const typeLabel = order.jobType || 'Order';

                return (
                  <div
                    key={order.id}
                    style={rowCard}
                    onClick={() => goToOrder(order.id)}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLDivElement).style.transform =
                        'translateY(-1px)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        '0 8px 18px rgba(0,0,0,0.35)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLDivElement).style.transform =
                        'translateY(0)';
                      (e.currentTarget as HTMLDivElement).style.boxShadow =
                        'none';
                    }}
                  >
                    <div style={rowLeft}>
                      <div style={rowTitle}>{dateLabel}</div>
                      <div style={rowMeta}>
                        {timeLabel} · {typeLabel}
                        {order.status && (
                          <span style={badge}>
                            {String(order.status).toLowerCase()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={caret}>&gt;</div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default TenantOrdersPage;
